import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

export function SignupPage() {
  const navigate = useNavigate();
  const { signUp, confirmSignUp, signIn, resendCode, isLoading, error, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [step, setStep] = useState<'signup' | 'confirm'>('signup');
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError(null);

    if (password !== confirmPassword) {
      setLocalError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }

    try {
      await signUp(email, password, displayName);
      setStep('confirm');
    } catch {
      // Error handled by store
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLocalError(null);

    try {
      await confirmSignUp(email, confirmationCode);
      // Log the user straight in and drop them into the app
      try {
        await signIn(email, password);
        navigate('/', { replace: true });
      } catch {
        // Verified but auto-login failed (e.g. password not in state) -> send to login
        navigate('/login', { state: { verified: true } });
      }
    } catch {
      // Error handled by store
    }
  };

  const handleResendCode = async () => {
    clearError();
    setResendSuccess(false);
    try {
      await resendCode(email);
      setResendSuccess(true);
    } catch {
      // Error handled by store
    }
  };

  if (step === 'confirm') {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="card max-w-md w-full">
          <h1 className="text-2xl font-bold text-primary mb-4 text-center">Verify Your Email</h1>
          <p className="text-text-light mb-6 text-center">
            We sent a verification code to <strong>{email}</strong>. Enter it below to activate your account.
          </p>

          <form onSubmit={handleConfirm} className="space-y-4">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-text-dark mb-1">
                Verification Code
              </label>
              <input
                id="code"
                type="text"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                className="input text-center text-2xl tracking-widest"
                placeholder="123456"
                required
                maxLength={6}
              />
            </div>

            {(error || localError) && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {localError || error}
              </div>
            )}

            {resendSuccess && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                New code sent! Check your email.
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full disabled:opacity-50"
            >
              {isLoading ? 'Verifying...' : 'Verify Account'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-text-light text-sm mb-2">Didn't receive the code?</p>
            <button
              onClick={handleResendCode}
              disabled={isLoading}
              className="text-secondary hover:text-primary font-medium text-sm"
            >
              Resend Code
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="card max-w-md w-full">
        <h1 className="text-2xl font-bold text-primary mb-6 text-center">
          Join the Hunt
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-text-dark mb-1">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
              placeholder="you@example.com"
              required
            />
          </div>

          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-text-dark mb-1">
              Display Name
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="input"
              placeholder="How you'll appear on leaderboards"
              required
              maxLength={50}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-text-dark mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
              placeholder="At least 8 characters"
              required
              minLength={8}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-dark mb-1">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              placeholder="Confirm your password"
              required
            />
          </div>

          {(error || localError) && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {localError || error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full disabled:opacity-50"
          >
            {isLoading ? 'Creating Account...' : 'Sign Up'}
          </button>
        </form>

        <p className="mt-6 text-center text-text-light">
          Already have an account?{' '}
          <Link to="/login" className="text-secondary hover:text-primary font-medium">
            Log In
          </Link>
        </p>
      </div>
    </div>
  );
}
